<template>
  <div class="bg-white rounded-lg border border-gray-200">
    <div class="px-4 py-4 bg-primary-50 border-b border-gray-100">
      <h4 class="text-sm font-semibold text-gray-900 flex items-center">
        <FontAwesomeIcon :icon="['fas', 'user']" class="mr-2 text-primary-600" />
        Task Owner
      </h4>
    </div>

    <div class="p-4">
      <div v-if="!isEditingOwner" class="flex items-center justify-between group">
        <div class="flex items-center space-x-4">
          <div class="relative">
            <div
              class="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold shadow-sm transition-transform duration-200 group-hover:scale-105"
              :style="{ backgroundColor: ownerColor }"
            >
              {{ ownerInitials }}
            </div>
            <div v-if="task.task_owner?.task_owner_name"
                 class="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
          </div>
          <div>
            <p class="text-sm font-medium text-gray-900">
              {{ task.task_owner?.task_owner_name || 'Unassigned' }}
            </p>
            <p class="text-xs text-gray-500">
              {{ task.task_owner?.task_owner_name ? 'Assigned Owner' : 'No owner assigned' }}
            </p>
          </div>
        </div>

        <button
          @click="startEditingOwner"
          class="px-4 py-2 text-xs font-medium text-primary-600 hover:text-primary-700
                 bg-primary-50 hover:bg-primary-100 rounded transition-all duration-200
                 opacity-0 group-hover:opacity-100 focus:opacity-100"
        >
          {{ task.task_owner?.task_owner_name ? 'Change' : 'Assign' }}
        </button>
      </div>

      <div v-else class="space-y-4">
        <div class="relative">
          <input
            v-model="ownerSearchQuery"
            @input="searchOwners"
            @focus="showOwnerDropdown = true"
            type="text"
            placeholder="Search for team member..."
            class="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg
                   focus:ring-2 focus:ring-blue-dark focus:ring-offset-4 focus:border-transparent
                   placeholder-gray-400 text-sm"
          />
          <FontAwesomeIcon
            v-if="!ownerSearchQuery"
            :icon="['fas', 'search']"
            class="absolute right-3 top-3 text-gray-400 text-sm"
          />
          <button v-else
                  @click="ownerSearchQuery = ''; searchOwners()"
                  class="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600">
            <FontAwesomeIcon :icon="['fas', 'times']" class="text-sm" />
          </button>

          <!-- Dropdown with owner suggestions -->
          <div v-if="showOwnerDropdown && (filteredOwners.length > 0 || ownerSearchQuery)"
               class="absolute z-10 w-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 max-h-48 overflow-auto">
            <button
              v-for="owner in filteredOwners"
              :key="owner.id"
              @click="selectOwner(owner)"
              class="w-full px-4 py-2 flex items-center space-x-4 hover:bg-gray-50
                     transition-colors duration-150 text-left group"
            >
              <div
                class="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold shrink-0"
                :style="{ backgroundColor: owner.color || '#6366F1' }"
              >
                {{ getInitials(owner.name) }}
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-sm font-medium text-gray-900 truncate sensitive">{{ owner.name }}</p>
                <p class="text-xs text-gray-500 truncate sensitive">{{ owner.email }}</p>
              </div>
              <FontAwesomeIcon
                :icon="['fas', 'chevron-right']"
                class="text-primary-600 opacity-0 group-hover:opacity-100 transition-opacity text-sm"
              />
            </button>

            <div v-if="ownerSearchQuery && filteredOwners.length === 0"
                 class="px-4 py-2 text-sm text-gray-500 text-center">
              No team members found
            </div>
          </div>
        </div>

        <div class="flex items-center space-x-2">
          <button
            @click="saveOwner"
            :disabled="!selectedOwner && !allowUnassign"
            class="flex-1 px-4 py-2 bg-primary-500
                   hover:bg-primary-600 disabled:bg-gray-400
                   text-white text-sm font-medium rounded transition-all duration-200
                   disabled:cursor-not-allowed"
          >
            {{ selectedOwner ? 'Assign' : 'Unassign' }}
          </button>
          <button
            @click="cancelEditingOwner"
            class="flex-1 px-4 py-2 bg-white border border-gray-300 text-gray-700
                   text-sm font-medium rounded hover:bg-gray-50 transition-colors duration-200"
          >
            Cancel
          </button>
        </div>

        <!-- Option to unassign -->
        <div v-if="task.task_owner?.task_owner_name" class="pt-2 border-t border-gray-100">
          <button
            @click="unassignOwner"
            class="w-full px-4 py-2 text-xs font-medium text-red-600 hover:text-red-700
                   bg-red-50 hover:bg-red-100 rounded transition-all duration-200"
          >
            Remove Current Owner
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import axios from 'axios';
import { mapGetters } from 'vuex';

export default {
  name: 'Task_Owner_Widget',
  components: {
    FontAwesomeIcon
  },
  props: {
    task: {
      type: Object,
      required: true
    }
  },
  emits: ['owner-updated'],
  data() {
    return {
      isEditingOwner: false,
      ownerSearchQuery: '',
      showOwnerDropdown: false,
      selectedOwner: null,
      availableOwners: [],
      filteredOwners: [],
      allowUnassign: false,
      loadingOwners: false,
    }
  },
  computed: {
    ...mapGetters('auth', ['current_user']),
    ownerInitials() {
      if (this.task.task_owner?.task_owner_name) {
        return this.getInitials(this.task.task_owner?.task_owner_name);
      }
      return '?';
    },
    ownerColor() {
      // Use the color from metadata if available, otherwise generate based on name
      if (this.task.task_owner?.task_owner_color) {
        return this.task.task_owner?.task_owner_color;
      }
      if (this.task.task_owner?.task_owner_name) {
        // Generate a color based on the name
        const colors = ['#6366F1', '#8B5CF6', '#EC4899', '#10B981', '#F59E0B', '#EF4444', '#3B82F6', '#14B8A6'];
        const index = this.task.task_owner?.task_owner_name.charCodeAt(0) % colors.length;
        return colors[index];
      }
      return '#9CA3AF'; // Gray for unassigned
    }
  },
  methods: {
    getInitials(name) {
      if (!name) return '?';
      const parts = name.split(/[,\s]+/).filter(Boolean);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    },

    startEditingOwner() {
      this.isEditingOwner = true;
      this.selectedOwner = null;
      this.ownerSearchQuery = '';
      this.loadAvailableOwners();
    },

    cancelEditingOwner() {
      this.isEditingOwner = false;
      this.selectedOwner = null;
      this.ownerSearchQuery = '';
      this.showOwnerDropdown = false;
    },

    async loadAvailableOwners() {
      this.loadingOwners = true;
      try {
        // Fetch available team members from your API
        const response = await axios.get('api/user/');
        this.availableOwners = response.data.map(user => ({
          id: user.id,
          name: `${user.last_name}, ${user.first_name}`,
          email: user.email || '',
          color: user.color || '#6366F1'
        }));
        this.filteredOwners = [...this.availableOwners];
      } catch (error) {
        console.error('Error loading owners:', error);
        this.availableOwners = [];
        this.filteredOwners = [];
      } finally {
        this.loadingOwners = false;
      }
    },

    searchOwners() {
      if (!this.ownerSearchQuery) {
        this.filteredOwners = [...this.availableOwners];
        return;
      }

      const query = this.ownerSearchQuery.toLowerCase();
      this.filteredOwners = this.availableOwners.filter(owner =>
        owner.name.toLowerCase().includes(query) ||
        (owner.email && owner.email.toLowerCase().includes(query))
      );
    },

    selectOwner(owner) {
      this.selectedOwner = owner;
      this.ownerSearchQuery = owner.name;
      this.showOwnerDropdown = false;
    },

    async saveOwner() {
      if (!this.selectedOwner) return;

      try {
        // Call the API to update task owner
        await axios.post(`api/tasks/${this.task.id}/assign_owner/`, {
          owner_id: this.selectedOwner.id
        });

        // Emit event to parent to update task data
        this.$emit('owner-updated', this.selectedOwner);

        // Close the editor
        this.cancelEditingOwner();
      } catch (error) {
        console.error('Error assigning owner:', error);
      }
    },

    async unassignOwner() {
      try {
        // Call the API to remove task owner
        await axios.post(`api/tasks/${this.task.id}/unassign_owner/`);

        // Emit event to parent to update task data
        this.$emit('owner-updated', null);

        // Close the editor
        this.cancelEditingOwner();
      } catch (error) {
        console.error('Error unassigning owner:', error);
      }
    },

    handleClickOutside(e) {
      if (!this.$el.contains(e.target)) {
        this.showOwnerDropdown = false;
      }
    }
  },
  mounted() {
    // Close dropdown when clicking outside
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  }
}
</script>

<style scoped>
/* Custom scrollbar for dropdown */
.max-h-48 {
  scrollbar-width: thin;
  scrollbar-color: #CBD5E1 #F3F4F6;
}

.max-h-48::-webkit-scrollbar {
  width: 6px;
}

.max-h-48::-webkit-scrollbar-track {
  background: #F3F4F6;
}

.max-h-48::-webkit-scrollbar-thumb {
  background-color: #CBD5E1;
  border-radius: 3px;
}

.max-h-48::-webkit-scrollbar-thumb:hover {
  background-color: #94A3B8;
}
</style>
